// PUSH 2 — Offentlig turkatalog «Alle turer» (/o/:token). Ingen innlogging,
// ingen betaling. Tenant + turer resolves FROM den stabile katalog-tokenet.
// Brandet responsivt grid; hvert kort lenker til turens egen /p/:token-side.
// Data-minimering: kun antall ledige seter per tur — aldri deltakernavn.
// Filtre (måned + avreisested) vises når listen blir lang (> 3 turer).
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, Users, CalendarDays, MapPin, ArrowRight, Ticket, AlertTriangle, Filter,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePublicLanguage } from '@/components/public/usePublicLanguage';
import PublicLanguageToggle from '@/components/public/PublicLanguageToggle';

const API = process.env.REACT_APP_BACKEND_URL;
const EMERALD = '#059669';

const localeOf = (lang: string) => (lang === 'en' ? 'en-GB' : 'nb-NO');
const fmtDate = (iso?: string | null, lang = 'no') => {
  if (!iso) return null;
  try {
    return new Date(iso.slice(0, 10)).toLocaleDateString(localeOf(lang), { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const monthLabel = (k: string, lang = 'no') => {
  const [y, m] = k.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(localeOf(lang), { month: 'long', year: 'numeric' });
};

const hexToRgba = (hex: string, a: number) => {
  const m = (hex || '').replace('#', '');
  if (m.length !== 6) return `rgba(5,150,105,${a})`;
  const r = parseInt(m.slice(0, 2), 16), g = parseInt(m.slice(2, 4), 16), b = parseInt(m.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

export default function PublicOpenTripsCatalogPage() {
  const { t: tt } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const { lang, setPublicLanguage } = usePublicLanguage(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [monthFilter, setMonthFilter] = useState('all');
  const [locFilter, setLocFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/open-trips/catalog/${token}`);
        if (!res.ok) throw new Error('bad');
        setData(await res.json());
      } catch { setErr(true); } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 relative" data-testid="catalog-error">
        <div className="absolute right-4 top-4"><PublicLanguageToggle lang={lang} onChange={setPublicLanguage} /></div>
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-slate-700">{tt('publicOpenTripsCatalogPage.katalogenErIkkeTilgjengelig', 'Katalogen er ikke tilgjengelig')}</h1>
          <p className="text-sm text-slate-500 mt-1">{tt('publicOpenTripsCatalogPage.sjekkAtDuHarApnet', 'Sjekk at du har åpnet riktig lenke.')}</p>
        </div>
      </div>
    );
  }

  const company = data.company || {};
  const brand = company.primary_color || EMERALD;
  const trips: any[] = data.trips || [];
  const closed = data.state === 'closed';

  const monthOpts = [...new Set(trips.filter((t) => t.trip_date).map((t) => String(t.trip_date).slice(0, 7)))].sort();
  const locOpts = [...new Set(trips.map((t) => t.from_location).filter(Boolean))].sort();
  const showFilters = trips.length > 3 && (monthOpts.length > 1 || locOpts.length > 1);
  const filtered = trips.filter((t) =>
    (monthFilter === 'all' || String(t.trip_date || '').slice(0, 7) === monthFilter) &&
    (locFilter === 'all' || t.from_location === locFilter));
  const hasActiveFilter = monthFilter !== 'all' || locFilter !== 'all';

  const selectCls = 'text-sm rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-700 outline-none focus:ring-2 focus:ring-[var(--brand)] focus:border-[var(--brand)]';

  return (
    <div className="min-h-screen bg-slate-50" style={{ ['--brand' as any]: brand }} data-testid="catalog-page">
      {/* Brandet header */}
      <header className="text-white" style={{ background: `linear-gradient(135deg, ${brand}, ${hexToRgba(brand, 0.78)})` }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="flex items-center gap-3 mb-6" data-testid="catalog-brand">
            {company.logo ? (
              <img src={company.logo} alt={company.name} className="h-10 w-auto max-w-[160px] object-contain" />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center font-bold">{(company.name || 'T')[0]}</div>
            )}
            {company.name && <span className="text-sm font-semibold text-white/90">{company.name}</span>}
            <div className="ml-auto"><PublicLanguageToggle lang={lang} onChange={setPublicLanguage} tone="dark" /></div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">{tt('publicOpenTripsCatalogPage.kommendeTurer', 'Kommende turer')}</h1>
          <p className="text-white/85 mt-2 text-sm sm:text-base max-w-2xl">{tt('publicOpenTripsCatalogPage.bliMedPaVareApne', 'Bli med på våre åpne turer, meld deg på direkte. Ingen betaling her, faktureres etter turen.')}</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {closed || trips.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="catalog-empty">
            <Ticket className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-slate-700">{tt('publicOpenTripsCatalogPage.ingenTurerAkkuratNa', 'Ingen turer akkurat nå')}</h2>
            <p className="text-sm text-slate-500 mt-1">{tt('publicOpenTripsCatalogPage.detErIngenApneTurer', 'Det er ingen åpne turer å melde seg på for øyeblikket. Kom gjerne tilbake senere.')}</p>
          </div>
        ) : (
          <>
            {showFilters && (
              <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="catalog-filters">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500"><Filter className="w-4 h-4" /> {tt('publicOpenTripsCatalogPage.filtrer', 'Filtrer')}</span>
                {monthOpts.length > 1 && (
                  <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className={selectCls} data-testid="catalog-filter-month" aria-label={tt('publicOpenTripsCatalogPage.filtrerPaManed', 'Filtrer på måned')}>
                    <option value="all">{tt('publicOpenTripsCatalogPage.alleManeder', 'Alle måneder')}</option>
                    {monthOpts.map((k) => <option key={k} value={k}>{monthLabel(k, lang)}</option>)}
                  </select>
                )}
                {locOpts.length > 1 && (
                  <select value={locFilter} onChange={(e) => setLocFilter(e.target.value)} className={selectCls} data-testid="catalog-filter-location" aria-label={tt('publicOpenTripsCatalogPage.filtrerPaAvreisested', 'Filtrer på avreisested')}>
                    <option value="all">{tt('publicOpenTripsCatalogPage.alleAvreisesteder', 'Alle avreisesteder')}</option>
                    {locOpts.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                )}
                {hasActiveFilter && (
                  <button onClick={() => { setMonthFilter('all'); setLocFilter('all'); }} className="text-sm underline text-slate-500 hover:text-slate-700" data-testid="catalog-filter-clear">{tt('publicOpenTripsCatalogPage.nullstill', 'Nullstill')}</button>
                )}
                <span className="text-sm text-slate-400 ml-auto">{tt('publicOpenTripsCatalogPage.avTurer', { defaultValue: '{{v1}} av {{v2}} turer', v1: filtered.length, v2: trips.length })}</span>
              </div>
            )}

            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="catalog-no-match">
                <p className="text-sm text-slate-500">{tt('publicOpenTripsCatalogPage.ingenTurerMatcherFilteret', 'Ingen turer matcher filteret.')} <button onClick={() => { setMonthFilter('all'); setLocFilter('all'); }} className="underline">{tt('publicOpenTripsCatalogPage.nullstill', 'Nullstill')}</button></p>
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
                {filtered.map((t, i) => {
                  const isFull = (t.seats_left ?? 0) <= 0;
                  return (
                    <a key={i} href={t.signup_url} data-testid={`catalog-card-${i}`}
                      className="group bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col transition-all hover:shadow-lg hover:-translate-y-0.5">
                      <div className="relative h-44 overflow-hidden bg-slate-100">
                        {t.image_url ? (
                          <img src={t.image_url} alt={t.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        ) : (
                          <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${brand}, ${hexToRgba(brand, 0.7)})` }} />
                        )}
                        <div className="absolute top-3 left-3">
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white shadow"
                            style={{ backgroundColor: isFull ? 'rgba(15,23,42,0.75)' : brand }}>
                            {isFull ? tt('publicOpenTripsCatalogPage.fulltVenteliste', 'Fullt (venteliste)') : tt('publicOpenTripsCatalogPage.nPlasserLedig', '{{count}} plasser ledig', { count: t.seats_left })}
                          </span>
                        </div>
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="text-lg font-bold text-slate-900 leading-snug">{t.title}</h3>
                        <div className="mt-2.5 space-y-1.5 text-sm text-slate-600">
                          {t.trip_date && <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-slate-400" />{fmtDate(t.trip_date, lang)}{t.departure_time ? ` · ${tt('common.atTime', 'kl.')} ${t.departure_time}` : ''}</div>}
                          {(t.from_location || t.to_location) && (
                            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" />{t.from_location}{t.to_location ? ` → ${t.to_location}` : ''}</div>
                          )}
                        </div>
                        <div className="mt-auto pt-4 flex items-center justify-between">
                          {t.price_per_seat > 0 ? (
                            <span className="text-sm font-semibold" style={{ color: brand }}>{t.price_per_seat} {t.currency || 'NOK'}<span className="text-slate-400 font-normal">{tt('publicOpenTripsCatalogPage.sete', '/sete')}</span></span>
                          ) : <span className="text-xs text-slate-400">{tt('publicOpenTripsCatalogPage.uforpliktende', 'Uforpliktende')}</span>}
                          <span className="inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all" style={{ color: brand }}>
                            {tt('publicOpenTripsCatalogPage.meldDegPa', 'Meld deg på')} <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-center text-xs text-slate-400">
        <Users className="w-4 h-4 inline mr-1 -mt-0.5" /> {tt('publicOpenTripsCatalogPage.pameldingHos', 'Påmelding hos')} {company.name || tt('publicOpenTripsCatalogPage.operatoren', 'operatøren')} · {tt('publicOpenTripsCatalogPage.ingenBetalingHerFaktureres', 'Ingen betaling her, faktureres etter turen.')}
      </footer>
    </div>
  );
}
